/**
 * Session ID Manager
 * Extracted from WebSocketSingleton.ts
 * Manages WebSocket session ID generation and storage
 */

import { log } from "@/shared/utils/logger";

const SESSION_STORAGE_KEY = "ws_session_id";

export class SessionIdManager {
  private sessionId: string | null = null;
  private canRestoreSession = true;

  /**
   * Get or create a stable session ID that persists across component re-renders
   */
  getStableSessionId(): string {
    if (!this.sessionId) {
      // Check if we have a stored session ID
      let storedSessionId: string | null = null;
      if (this.canRestoreSession) {
        try {
          storedSessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
        } catch (error) {
          log.warn("SessionIdManager", "Session storage unavailable:", error);
        }
      }

      if (storedSessionId) {
        this.sessionId = storedSessionId;
        log.debug(
          "SessionIdManager",
          "Using stored session ID:",
          this.sessionId
        );
      } else {
        // Generate new session ID only if none exists
        this.sessionId = this.generateSessionId();
        this.persistSession();
        log.info(
          "SessionIdManager",
          "Created new session ID:",
          this.sessionId
        );
      }
    }

    return this.sessionId;
  }

  /**
   * Generate a fresh session ID
   * Called on new connections to prevent staleness
   */
  generateFreshSessionId(): string {
    this.sessionId = this.generateSessionId();
    this.persistSession();
    log.info(
      "SessionIdManager",
      "Generated fresh session ID:",
      this.sessionId
    );
    return this.sessionId;
  }

  /**
   * Generate a new unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private persistSession(): void {
    if (!this.sessionId) return;
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, this.sessionId);
    } catch (error) {
      log.warn("SessionIdManager", "Keeping session in memory:", error);
    }
  }

  /**
   * Get current session ID (may be null if not initialized)
   */
  getCurrentSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Clear the session ID
   */
  clearSession(): void {
    this.sessionId = null;
    this.canRestoreSession = false;
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      log.warn("SessionIdManager", "Failed to remove stored session:", error);
    }
    log.info("SessionIdManager", "Session cleared");
  }

  /**
   * Check if a session ID exists
   */
  hasSession(): boolean {
    return this.sessionId !== null;
  }
}
